import React, { useState, useEffect } from 'react';
import EventForm from './EventForm';
import SwipeableEventItem from './SwipeableEventItem';

function History({ user }) {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventType, setEventType] = useState('');
  const [showFoodsList, setShowFoodsList] = useState(false);
  const [introducedFoods, setIntroducedFoods] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (filter === 'all') {
      setFilteredEvents(events);
    } else {
      setFilteredEvents(events.filter(event => event.type === filter));
    }
    calculateStats();
  }, [events, filter]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/events', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        fetchEvents();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventType(event.type);
    setShowForm(true);
  };

  const handleEventSaved = () => {
    setShowForm(false);
    setEditingEvent(null);
    setEventType('');
    fetchEvents();
  };


  const calculateStats = () => {
    const today = new Date().toDateString();
    const todayEvents = events.filter(event => 
      new Date(event.created_at).toDateString() === today
    );

    const feedingEvents = todayEvents.filter(e => e.type === 'feeding');
    const diaperEvents = todayEvents.filter(e => e.type === 'diaper');
    const tempEvents = events.filter(e => e.type === 'temperature');
    const bathEvents = events.filter(e => e.type === 'bath');

    let totalMilk = 0;
    let breastfeedingDuration = 0;
    let urineCount = 0;
    let stoolCount = 0;

    feedingEvents.forEach(event => {
      const data = JSON.parse(event.data);
      if (data.mode === 'biberon') {
        totalMilk += data.amount_ml;
      }
    });

    diaperEvents.forEach(event => {
      const data = JSON.parse(event.data);
      if (data.urine) urineCount++;
      if (data.stool) stoolCount++;
    });

    const lastTemp = tempEvents.length > 0 ?
      JSON.parse(tempEvents[tempEvents.length - 1].data).value : null;

    const lastBath = bathEvents.length > 0 ?
      new Date(bathEvents.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0].created_at) : null;

    // Statistiques diversification (aliments dans les événements feeding)
    // Construire la liste des aliments introduits avec leur date et réaction
    const foodsMap = new Map();

    events.filter(e => e.type === 'feeding').forEach(e => {
      const d = JSON.parse(e.data);

      // Nouveau format multi-aliments
      if (d.foods && Array.isArray(d.foods)) {
        d.foods.forEach(food => {
          if (!foodsMap.has(food.name)) {
            foodsMap.set(food.name, {
              name: food.name,
              emoji: food.emoji || '🥄',
              firstDate: e.created_at,
              reaction: food.reaction
            });
          }
        });
      }
      // Ancien format mono-aliment (rétrocompatibilité)
      else if (d.food_name) {
        if (!foodsMap.has(d.food_name)) {
          foodsMap.set(d.food_name, {
            name: d.food_name,
            emoji: d.food_emoji || '🥄',
            firstDate: e.created_at,
            reaction: d.reaction
          });
        }
      }
    });

    const foodsList = Array.from(foodsMap.values()).sort((a, b) =>
      new Date(b.firstDate) - new Date(a.firstDate)
    );
    setIntroducedFoods(foodsList);

    const uniqueFoods = foodsMap.size;

    setStats({
      totalBottles: feedingEvents.filter(e => JSON.parse(e.data).mode === 'biberon').length,
      totalMilk,
      urineCount,
      stoolCount,
      lastTemp,
      lastBath: lastBath ? lastBath.toLocaleDateString('fr-FR') : 'Jamais',
      totalFoodsIntroduced: uniqueFoods
    });
  };

  const formatTimeForHistory = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };




  const groupEventsByDate = (events) => {
    const grouped = {};
    events.forEach(event => {
      const date = new Date(event.created_at).toLocaleDateString('fr-FR');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(event);
    });
    return grouped;
  };

  const groupedEvents = groupEventsByDate(filteredEvents);

  if (showForm) {
    return (
      <EventForm
        type={eventType}
        event={editingEvent}
        onSave={handleEventSaved}
        onCancel={() => {
          setShowForm(false);
          setEditingEvent(null);
        }}
      />
    );
  }

  return (
    <div className="p-4 pb-20">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">📊 Historique</h1>

      {/* Statistiques du jour */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Aujourd'hui
        </h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-600">Biberons</div>
            <div className="font-semibold">{stats.totalBottles} ({stats.totalMilk}ml)</div>
          </div>
          <div>
            <div className="text-gray-600">Couches</div>
            <div className="font-semibold">{stats.urineCount} urine, {stats.stoolCount} selles</div>
          </div>
          <div>
            <div className="text-gray-600">Dernière T°</div>
            <div className="font-semibold">{stats.lastTemp ? `${stats.lastTemp}°C` : 'Aucune'}</div>
          </div>
          <div>
            <div className="text-gray-600">Dernier bain</div>
            <div className="font-semibold">{stats.lastBath}</div>
          </div>
          <div
            className="cursor-pointer"
            onClick={() => setShowFoodsList(!showFoodsList)}
          >
            <div className="text-gray-600 flex items-center">
              Aliments introduits
              <svg
                className={`w-4 h-4 ml-1 transition-transform ${showFoodsList ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            <div className="font-semibold text-orange-600">{stats.totalFoodsIntroduced || 0} aliment{stats.totalFoodsIntroduced > 1 ? 's' : ''}</div>
          </div>
        </div>

        {/* Liste dépliable des aliments introduits */}
        {showFoodsList && introducedFoods.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">🥕 Liste des aliments introduits</h3>
            <div className="flex flex-wrap gap-2">
              {introducedFoods.map((food, index) => (
                <div
                  key={index}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                    food.reaction === 'allergy'
                      ? 'bg-red-100 text-red-800'
                      : food.reaction === 'disliked'
                      ? 'bg-gray-100 text-gray-600'
                      : food.reaction === 'liked'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                  title={`Introduit le ${new Date(food.firstDate).toLocaleDateString('fr-FR')}`}
                >
                  <span className="mr-1">{food.emoji}</span>
                  <span>{food.name}</span>
                  {food.reaction === 'liked' && <span className="ml-1">😋</span>}
                  {food.reaction === 'disliked' && <span className="ml-1">😖</span>}
                  {food.reaction === 'allergy' && <span className="ml-1">⚠️</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filtres */}
      <div className="mb-6">
        <div className="flex space-x-2 overflow-x-auto">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              filter === 'all' 
                ? 'bg-primary-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Tout
          </button>
          <button
            onClick={() => setFilter('feeding')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              filter === 'feeding' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🍼 Repas
          </button>
          <button
            onClick={() => setFilter('diaper')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              filter === 'diaper' 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            👶 Couches
          </button>
          <button
            onClick={() => setFilter('temperature')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              filter === 'temperature' 
                ? 'bg-red-500 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            🌡️ Température
          </button>
          <button
            onClick={() => setFilter('bath')}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap ${
              filter === 'bath'
                ? 'bg-purple-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            💧 Bains
          </button>
        </div>
      </div>

      {/* Liste des événements */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      ) : Object.keys(groupedEvents).length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Aucun événement trouvé
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedEvents)
            .sort(([a], [b]) => new Date(b) - new Date(a))
            .map(([date, dateEvents]) => (
              <div key={date} className="bg-white rounded-lg shadow">
                <div className="p-3 border-b bg-gray-50 rounded-t-lg">
                  <h3 className="font-medium text-gray-900">{date}</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {dateEvents
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((event) => (
                      <SwipeableEventItem
                        key={event.id}
                        event={event}
                        onEdit={handleEditEvent}
                        onDelete={handleDeleteEvent}
                        timeFormatter={formatTimeForHistory}
                      />
                    ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default History;